import { cvMedications } from '../cv-medications';
import { obMedications } from '../ob-medications';
import { regionalMedications } from '../regional-medications';
import { pediatricMedications } from '../pediatric-medications';

/*
 * One shape for every drug in the Medication Reference, built from the four legacy
 * data files until they are rewritten against cited sources.
 *
 *   drug = {
 *     id, name, brand, category, cls, clsLabel,
 *     doses:      [{ label, text }]        what you draw up; mg/kg lines drive the calculator
 *     receptors?: { 'α1': '+++', ... }     vasoactives only, shown on the card
 *     max?:       [{ label, text }]        local anesthetics
 *     cautions:   [string]                 the few that matter at the bedside
 *     pharm:      { pk: [{label,text}], pd: [{label,text}], uses: [string], notes: [string] }  behind "Pharmacology"
 *     source?:    { title, href? }         not yet set for the legacy content
 *   }
 */

export const CATEGORIES = [
  { id: 'cardiovascular', label: 'CV' },
  { id: 'obstetric', label: 'OB' },
  { id: 'regional', label: 'Regional' },
  { id: 'pediatric', label: 'Pediatric' },
];

export const CLASSES = {
  cardiovascular: [
    { id: 'vasopressor', label: 'Pressors' },
    { id: 'inotrope', label: 'Inotropes' },
    { id: 'vasodilator', label: 'Vasodilators' },
    { id: 'antiarrhythmic', label: 'Antiarrhythmics' },
  ],
  obstetric: [
    { id: 'uterotonic', label: 'Uterotonics' },
    { id: 'neuraxial-opioid', label: 'Neuraxial opioids' },
    { id: 'neuraxial-adjunct', label: 'Neuraxial adjuncts' },
    { id: 'preeclampsia', label: 'Preeclampsia' },
  ],
  regional: [
    { id: 'amide-local', label: 'Amides' },
    { id: 'ester-local', label: 'Esters' },
    { id: 'adjunct', label: 'Adjuncts' },
  ],
  pediatric: [
    { id: 'induction', label: 'Induction' },
    { id: 'opioid', label: 'Opioids' },
    { id: 'muscle-relaxant', label: 'Relaxants' },
    { id: 'reversal', label: 'Reversal' },
    { id: 'emergency', label: 'Emergency' },
  ],
};

// Legacy class ids that fold into one of the classes above
const CLASS_ALIAS = { volatile: 'induction', sedative: 'emergency' };

const LABELS = {
  induction: 'Induction', infants: 'Infants', neonates: 'Neonates', neonate: 'Neonate', infusion: 'Infusion', sedation: 'Sedation',
  sedation_iv: 'Sedation IV', maintenance: 'Maintenance', bolus: 'Bolus', loading: 'Loading', im: 'IM', iv: 'IV', po: 'PO',
  intranasal: 'Intranasal', intubation: 'Intubation', rsi: 'RSI', reversal: 'Reversal', analgesia: 'Analgesia', premedication: 'Premedication',
  bradycardia: 'Bradycardia', moderate: 'Moderate', organophosphate: 'Organophosphate', withGlycopyrrolate: 'With glycopyrrolate',
  epidural: 'Epidural', intrathecal: 'Intrathecal', perineural: 'Perineural', testDose: 'Test dose', sublingual: 'Sublingual', repeat: 'Repeat',
  vfib: 'VF/pulseless VT', standard: 'Standard', alternative: 'Alternative', plain: 'Plain', withEpi: 'With epinephrine', tumescent: 'Tumescent',
  spinal: 'Spinal', mac: 'MAC', cns: 'CNS', cardiac: 'Cardiac', treatment: 'Treatment',
};
const label = (key) => LABELS[key] || key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
const rows = (obj, skip = ['units']) => Object.entries(obj || {}).filter(([k]) => !skip.includes(k)).map(([k, v]) => ({ label: label(k), text: String(v) }));

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const normalise = (m, category) => {
  const cls = CLASS_ALIAS[m.class] || m.class;
  const clsLabel = (CLASSES[category] || []).find((c) => c.id === cls)?.label || cls;
  const pk = [
    m.onset && { label: 'Onset', text: m.onset },
    m.duration && { label: 'Duration', text: m.duration },
    m.metabolism && { label: 'Metabolism', text: m.metabolism },
    m.pKa && { label: 'pKa', text: String(m.pKa) },
    m.proteinBinding && { label: 'Protein binding', text: String(m.proteinBinding) },
  ].filter(Boolean);
  const pd = rows(m.hemodynamics);
  if (m.mac) pd.push(...rows(m.mac).map((r) => ({ label: `MAC, ${r.label.toLowerCase()}`, text: r.text })));
  const cautions = (m.considerations || []).slice(0, 4);
  const notes = (m.considerations || []).slice(4);
  if (m.toxicity) notes.push(...rows(m.toxicity).map((r) => ({ ...r })).map((r) => `${r.label} toxicity: ${r.text}`));
  return {
    id: `${category}-${slug(m.name)}`,
    name: m.name.replace(/\s*\((Pediatric|Neuraxial|Preeclampsia|Perineural|Local Anesthetic Adjunct)\)$/, ''),
    brand: m.proprietaryName && m.proprietaryName !== 'N/A' ? m.proprietaryName : null,
    category,
    cls,
    clsLabel,
    doses: [...rows(m.dosing), ...rows(m.concentration).map((r) => ({ label: `Concentration, ${r.label.toLowerCase()}`, text: r.text }))],
    receptors: m.receptors && (cls === 'vasopressor' || cls === 'inotrope') ? m.receptors : null,
    max: m.maxDose ? rows(m.maxDose) : null,
    bestFor: m.bestFor || null,
    cautions,
    pharm: { pk, pd, uses: m.uses || [], notes },
    source: null,
  };
};

export const DRUGS = [
  ...cvMedications.map((m) => normalise(m, 'cardiovascular')),
  ...obMedications.map((m) => normalise(m, 'obstetric')),
  ...regionalMedications.map((m) => normalise(m, 'regional')),
  ...pediatricMedications.map((m) => normalise(m, 'pediatric')),
];

export const drugsIn = (category, cls) => DRUGS.filter((d) => d.category === category && (!cls || d.cls === cls));
export const drugById = (id) => DRUGS.find((d) => d.id === id);

/** Drugs whose name, brand or class matches a free-text query, across every category */
export const searchDrugs = (query) => {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return DRUGS.filter((d) => [d.name, d.brand || '', d.clsLabel, d.category].some((s) => s.toLowerCase().includes(q)));
};

/*
 * Weight-based dosing. A dose string like "2.5-3.5 mg/kg IV" or "100-300 mcg/kg/min"
 * becomes "20–28 mg" for an 8 kg patient. Only per-kg expressions are computed;
 * anything else is left alone. Returns null when there is nothing to compute.
 */
const PER_KG = /(\d+(?:\.\d+)?)(?:\s*[-–]\s*(\d+(?:\.\d+)?))?\s*(mcg|mg|units?|g|mL)\s*\/\s*kg(\s*\/\s*(?:min|h|hr|hour))?/i;

const fmt = (n) => {
  if (n >= 100) return String(Math.round(n));
  if (n >= 10) return String(Math.round(n * 10) / 10);
  return String(Math.round(n * 100) / 100);
};

export const computeDose = (text, kg) => {
  if (!kg || kg <= 0) return null;
  const m = text.match(PER_KG);
  if (!m) return null;
  // Infusion rates are set per kg on the pump; computing a total per minute is not useful
  if (m[4]) return null;
  const lo = parseFloat(m[1]) * kg;
  const hi = m[2] ? parseFloat(m[2]) * kg : null;
  const unit = m[3].replace(/^units?$/i, 'units');
  const rate = m[4] ? m[4].replace(/\s/g, '').replace('/hr', '/h').replace('/hour', '/h') : '';
  // Respect an explicit cap such as "(max 300 mg)" in the same string
  const cap = text.match(/max(?:imum)?\s*(\d+(?:\.\d+)?)\s*(mcg|mg|units?|g)/i);
  const capVal = cap && cap[2].toLowerCase() === unit.toLowerCase() ? parseFloat(cap[1]) : null;
  const clamp = (v) => (capVal != null ? Math.min(v, capVal) : v);
  const a = clamp(lo);
  const b = hi != null ? clamp(hi) : null;
  return `${fmt(a)}${b != null && b !== a ? `–${fmt(b)}` : ''} ${unit}${rate}`;
};
